import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  CarFront,
  FileText,
  Home,
  Map,
  Package,
  Settings,
  Shirt,
  Users,
} from "lucide-react";
import type { UserRole } from "@/lib/api-types";

export type NavigationItem = {
  href: string;
  label: string;
  description: string;
  icon: LucideIcon;
  comingSoon?: boolean;
  matchPrefixes?: string[];
  roles?: UserRole[];
};

export const navigationSections: Array<{
  title: string;
  items: NavigationItem[];
}> = [
  {
    title: "Overview",
    items: [
      {
        href: "/dashboard",
        label: "Dashboard",
        description: "Tổng quan catalog, pricing và recent uploads.",
        icon: Home,
      },
      {
        href: "/analytics",
        label: "Analytics",
        description: "Phân tích phân bố collection, component và coverage.",
        icon: BarChart3,
      },
      {
        href: "/logs",
        label: "Event logs",
        description: "Truy vấn và kiểm tra event stream theo filter.",
        icon: FileText,
      },
    ],
  },
  {
    title: "Asset Library",
    items: [
      {
        href: "/assets/clothes",
        label: "Clothes",
        description: "Duyệt mesh part, texture và pricing theo asset.",
        icon: Shirt,
        matchPrefixes: ["/assets/clothes", "/render"],
      },
      {
        href: "/assets/vehicles",
        label: "Vehicles",
        description: "Reserved for the next asset module.",
        icon: CarFront,
        comingSoon: true,
      },
      {
        href: "/assets/objects",
        label: "Objects",
        description: "Reserved for the next asset module.",
        icon: Package,
        comingSoon: true,
      },
      {
        href: "/assets/maps",
        label: "Maps",
        description: "Reserved for the next asset module.",
        icon: Map,
        comingSoon: true,
      },
    ],
  },
  {
    title: "System",
    items: [
      {
        href: "/settings",
        label: "Settings",
        description: "Runtime state, endpoints và integration snippets.",
        icon: Settings,
      },
      {
        href: "/users",
        label: "Users",
        description: "Quản lý tài khoản, role và trạng thái hoạt động.",
        icon: Users,
        roles: ["admin"],
      },
    ],
  },
];

type RouteMeta = {
  title: string;
  description: string;
};

const routeMetaEntries: Array<{
  matches: (pathname: string) => boolean;
  meta: RouteMeta;
}> = [
  {
    matches: (pathname) => pathname === "/dashboard",
    meta: {
      title: "Dashboard",
      description: "Theo dõi sức khỏe catalog và trạng thái pricing từ một màn hình gọn.",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/analytics"),
    meta: {
      title: "Analytics",
      description: "Xem phân bố collection, component và mức độ phủ dữ liệu hiện tại.",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/logs"),
    meta: {
      title: "Event logs",
      description: "Lọc, so sánh và kiểm tra event stream theo actor, resource và labels.",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/assets/clothes") || pathname.startsWith("/render"),
    meta: {
      title: "Clothes library",
      description: "",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/assets/vehicles"),
    meta: {
      title: "Vehicles",
      description: "Module chưa triển khai. Route này đang giữ chỗ cho asset type tiếp theo.",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/assets/objects"),
    meta: {
      title: "Objects",
      description: "Module chưa triển khai. Route này đang giữ chỗ cho asset type tiếp theo.",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/assets/maps"),
    meta: {
      title: "Maps",
      description: "Module chưa triển khai. Route này đang giữ chỗ cho asset type tiếp theo.",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/users"),
    meta: {
      title: "User management",
      description: "Kiểm soát role và quyền truy cập của tài khoản trong admin workspace.",
    },
  },
  {
    matches: (pathname) => pathname.startsWith("/settings"),
    meta: {
      title: "Runtime settings",
      description: "Minh bạch runtime state, endpoint exposure và integration contracts.",
    },
  },
];

export function getRouteMeta(pathname: string): RouteMeta {
  const matched = routeMetaEntries.find((entry) => entry.matches(pathname));
  if (matched) {
    return matched.meta;
  }
  return {
    title: "Workspace",
    description: "Quản lý catalog và pipeline dữ liệu trong một admin shell gọn.",
  };
}

export function isNavigationItemActive(
  item: NavigationItem,
  pathname: string,
): boolean {
  const prefixes = item.matchPrefixes ?? [item.href];
  return prefixes.some((prefix) => {
    if (prefix === "/") {
      return pathname === "/";
    }
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}
